import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CHAT,
    PANEL_NAMES,
    PATH_TO_PANEL,
    closeOccupant as closeIn,
    decodeWorkspace,
    encodeWorkspace,
    isOccupantOpen,
    occupantOrder,
    openOccupant as openIn,
    setExpandedOccupant as expandIn,
    toUrl,
    toggleOccupant as toggleIn,
} from '../utils/workspaceSlots';

export { PANEL_NAMES, PATH_TO_PANEL, CHAT };

/**
 * The workspace's two view slots, derived from (and written back to) the URL.
 *
 * All the RULES live in `utils/workspaceSlots.js` (pure, unit-tested); this hook
 * is only the router binding. See that module's header for the slot model and
 * the URL grammar.
 *
 *   /                          — the Main Chat alone
 *   /chat/:id                  — the Main Chat on a specific conversation
 *   /calendar                  — chat + Calendar (the pre-existing split)
 *   /calendar?chat=0           — Calendar alone (a launcher tile, #356)
 *   /calendar?with=boards      — Boards | Calendar, two features (#361)
 *   /calendar?view=expanded    — Calendar covers everything right of the sidebar
 *
 * The legacy surface (`activePanel`, `panelMode`, `openPanel`, `closePanel`,
 * `togglePanel`, `setPanelMode`) is kept verbatim so nothing outside the shell
 * had to change: `activePanel` is the occupant the PATHNAME names, which is the
 * one every existing caller meant.
 */
export function usePanelState() {
    const location = useLocation();
    const navigate = useNavigate();

    const workspace = useMemo(
        () => decodeWorkspace(location.pathname, location.search),
        [location.pathname, location.search],
    );

    const occupants = useMemo(() => occupantOrder(workspace), [workspace]);
    const openFeatures = workspace.features;
    const chatOpen = workspace.chatOpen;
    const expandedOccupant = workspace.expanded;

    // The pathname occupant — what every pre-slot-model caller called "the panel".
    const activePanel = openFeatures.length ? openFeatures[openFeatures.length - 1] : null;
    const panelMode = expandedOccupant ? 'expanded' : 'split';

    const conversationIdFromUrl = useMemo(() => {
        const match = location.pathname.match(/^\/chat\/([^/]+)/);
        return match ? match[1] : null;
    }, [location.pathname]);

    /**
     * Commit a next state to the URL. `preserveParams` carries the current query
     * string over (deep links survive an expand toggle); `extraParams` seeds a
     * fresh one (how Telegram/Rulebook open expanded).
     */
    const go = useCallback((next, {
        replace = false, returnToConversationId = null, extraParams = null, preserveParams = false,
    } = {}) => {
        let baseParams = null;
        if (preserveParams) baseParams = location.search;
        if (extraParams) {
            const p = new URLSearchParams(baseParams || '');
            for (const [k, v] of Object.entries(extraParams)) {
                if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
            }
            baseParams = p.toString();
        }
        const encoded = encodeWorkspace(next, {
            chatPath: returnToConversationId ? `/chat/${returnToConversationId}` : '/',
            baseParams,
        });
        navigate(toUrl(encoded), { replace });
    }, [navigate, location.search]);

    /**
     * `extraParams: { view: 'expanded' }` is how the Rulebook asks to open
     * full-width (Telegram stopped forcing it with #444). Expansion is STATE now, not a raw query param — and
     * `encodeWorkspace` rewrites the control params from that state — so a caller's
     * `view` has to be translated into the state or it is silently dropped and the
     * panel opens in a split column instead.
     */
    const withRequestedExpansion = useCallback((next, panelName, opts) => {
        if (opts?.extraParams?.view !== 'expanded') return next;
        return expandIn(next, panelName);
    }, []);

    const openPanel = useCallback((panelName, opts = {}) => {
        if (!panelName) return;
        go(withRequestedExpansion(openIn(workspace, panelName), panelName, opts), opts);
    }, [go, workspace, withRequestedExpansion]);

    /** Close ONE occupant by name (a panel's X, or the chat's — #361). */
    const closeOccupant = useCallback((name, opts = {}) => {
        go(closeIn(workspace, name), opts);
    }, [go, workspace]);

    /**
     * Legacy: "close the panel". Closes the pathname occupant when there is one,
     * so a panel's X behaves exactly as it always did (the chat is left behind);
     * with no panel open it closes the chat, which empties the workspace and
     * therefore lands on the launcher (#361).
     */
    const closePanel = useCallback((opts = {}) => {
        closeOccupant(activePanel || CHAT, opts);
    }, [closeOccupant, activePanel]);

    const togglePanel = useCallback((panelName, opts = {}) => {
        if (!panelName) { closePanel(opts); return; }
        go(withRequestedExpansion(toggleIn(workspace, panelName), panelName, opts), opts);
    }, [go, workspace, closePanel, withRequestedExpansion]);

    const toggleChat = useCallback((opts = {}) => {
        go(toggleIn(workspace, CHAT), opts);
    }, [go, workspace]);

    /** Expand ONE occupant, or collapse everything with `null` (#359). */
    const setExpandedOccupant = useCallback((name) => {
        go(expandIn(workspace, name), { replace: true, preserveParams: true });
    }, [go, workspace]);

    /** Legacy: expand/collapse the pathname occupant. */
    const setPanelMode = useCallback((mode) => {
        if (!activePanel) return;
        setExpandedOccupant(mode === 'expanded' ? activePanel : null);
    }, [activePanel, setExpandedOccupant]);

    const isOpen = useCallback((name) => isOccupantOpen(workspace, name), [workspace]);

    return {
        // Slot model
        occupants,
        openFeatures,
        chatOpen,
        expandedOccupant,
        isOpen,
        closeOccupant,
        toggleChat,
        setExpandedOccupant,
        // Legacy surface
        activePanel,
        panelMode,
        conversationIdFromUrl,
        openPanel,
        closePanel,
        togglePanel,
        setPanelMode,
    };
}
